import { useEffect, useState } from 'react';
import Modal from './Modal';
import ReadOnlyPythonCode from './ReadOnlyPythonCode';
import api from '../api/client';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

const VERDICT_LABELS = {
  ACCEPTED: 'Принято',
  REVIEW_IN_LESSON: 'Посмотрим на занятии',
  REDO: 'Переделать',
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return '—';
  }
}

function hasWorkspaceState(ws) {
  if (!ws || typeof ws !== 'object') return false;
  return Object.keys(ws).length > 0;
}

/**
 * Просмотр сохранённых попыток текущей задачи (только чтение, редактор не трогаем).
 */
export default function TaskAttemptsHistoryModal({ open, onClose, taskSlug }) {
  const [attempts, setAttempts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !taskSlug) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setAttempts([]);
    setSelectedId(null);
    (async () => {
      try {
        const res = await api.get('/tasks/attempts/', { params: { task: taskSlug } });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        setAttempts(list);
        if (list.length) setSelectedId(list[0].id);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Не удалось загрузить попытки.');
        setAttempts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, taskSlug]);

  const selected = attempts.find((a) => a.id === selectedId) || null;
  const code = selected ? String(selected.code_snapshot ?? '').trim() : '';
  const textAnswer = selected
    ? String(selected.tests_result?.student_text_answer ?? '').trim()
    : '';
  const hasBoard = selected ? hasWorkspaceState(selected.workspace_state) : false;

  return (
    <Modal open={open} title="Попытки этой задачи" onClose={onClose}>
      <div className="task-attempts-history">
        {loading ? (
          <p className="text-secondary">Загрузка…</p>
        ) : error ? (
          <div className="alert alert-error">{error}</div>
        ) : attempts.length === 0 ? (
          <div className="task-attempts-history-empty">
            <p>Сохранённых попыток ещё нет.</p>
            <p className="text-secondary">
              Черновик в редакторе сюда не попадает. Попытка появляется при сохранении
              или при переходе к другой задаче.
            </p>
          </div>
        ) : (
          <div className="task-attempts-history-split">
            <aside className="task-attempts-history-list" aria-label="Список попыток">
              {attempts.map((a) => {
                const label = SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '—';
                const verdict = a.teacher_verdict
                  ? VERDICT_LABELS[a.teacher_verdict] || a.teacher_verdict
                  : '';
                const active = a.id === selectedId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    className={`task-attempts-history-row${active ? ' active' : ''}`}
                    onClick={() => setSelectedId(a.id)}
                  >
                    <div className="task-attempts-history-row-top">
                      <span className="text-secondary">{fmtDateTime(a.created_at)}</span>
                      <span className="task-attempts-history-report">{label}</span>
                    </div>
                    {(verdict || a.saved_by_teacher) && (
                      <div className="task-attempts-history-row-meta">
                        {verdict ? <span>{verdict}</span> : null}
                        {a.saved_by_teacher ? (
                          <span className="text-secondary">сохранил преподаватель</span>
                        ) : null}
                      </div>
                    )}
                  </button>
                );
              })}
            </aside>

            <main className="task-attempts-history-detail">
              {!selected ? (
                <p className="text-secondary">Выберите попытку слева.</p>
              ) : (
                <>
                  <div className="task-attempts-history-detail-head">
                    <span className="badge">
                      {SELF_REPORT_LABELS[selected.self_report_status]
                        || selected.self_report_status
                        || '—'}
                    </span>
                    <span className="text-secondary">{fmtDateTime(selected.created_at)}</span>
                    {selected.teacher_verdict ? (
                      <span className="badge">
                        {VERDICT_LABELS[selected.teacher_verdict] || selected.teacher_verdict}
                      </span>
                    ) : null}
                    {selected.saved_by_teacher ? (
                      <span className="text-secondary">сохранил преподаватель</span>
                    ) : null}
                  </div>

                  {selected.self_comment ? (
                    <div className="task-attempts-history-block">
                      <strong>Отчёт / комментарий</strong>
                      <div className="text-secondary task-attempts-history-pre">
                        {selected.self_comment}
                      </div>
                    </div>
                  ) : null}

                  {selected.teacher_comment ? (
                    <div className="task-attempts-history-block">
                      <strong>Комментарий преподавателя</strong>
                      <div className="text-secondary task-attempts-history-pre">
                        {selected.teacher_comment}
                      </div>
                    </div>
                  ) : null}

                  {code ? (
                    <div className="task-attempts-history-block">
                      <strong>Код</strong>
                      <ReadOnlyPythonCode value={code} className="task-attempts-history-code" />
                    </div>
                  ) : textAnswer ? (
                    <div className="task-attempts-history-block">
                      <strong>Ответ</strong>
                      <div className="text-secondary task-attempts-history-pre">{textAnswer}</div>
                    </div>
                  ) : hasBoard ? (
                    <p className="text-secondary">Сохранено состояние доски.</p>
                  ) : (
                    <p className="text-secondary">В этой попытке нет кода и ответа.</p>
                  )}
                </>
              )}
            </main>
          </div>
        )}
      </div>
    </Modal>
  );
}
